import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { get, post } from "../../utils/http";

const { Title } = Typography;

interface OrderRecord {
  id: number;
  order_no: string;
  user_id: number;
  product_type: string;
  module_id?: number | null;
  amount_cents: number;
  platform: string;
  channel: string;
  status: string;
  third_party_order_no?: string;
  third_party_transaction_id?: string;
  fail_reason?: string;
  paid_at?: string | null;
  provider_status?: string;
  refund_amount_cents?: number;
  created_at?: string | null;
}

const statusTagMap: Record<string, { color: string; text: string }> = {
  pending: { color: "processing", text: "待支付" },
  paid: { color: "success", text: "已支付" },
  closed: { color: "default", text: "已关闭" },
  failed: { color: "error", text: "失败" },
  refunded: { color: "volcano", text: "已退款" },
  revoked: { color: "red", text: "已撤销" },
  manual_review: { color: "warning", text: "待人工复核" },
};

const fenToYuan = (value?: number | null) =>
  typeof value === "number" ? (value / 100).toFixed(2) : "-";

const isAutoExpiredCloseReason = (reason?: string) =>
  typeof reason === "string" && reason.includes("订单超时未支付");

const renderCloseReason = (reason?: string) => {
  if (!reason) return "-";
  if (isAutoExpiredCloseReason(reason)) {
    return (
      <Space direction="vertical" size={4}>
        <Tag color="orange">系统自动关闭</Tag>
        <span style={{ color: "#8c8c8c", lineHeight: 1.5 }}>{reason}</span>
      </Space>
    );
  }
  return <span style={{ lineHeight: 1.5 }}>{reason}</span>;
};

function PaymentOrdersPage() {
  const [dataSource, setDataSource] = useState<OrderRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [queryForm] = Form.useForm();
  const [closeForm] = Form.useForm();
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<OrderRecord | null>(null);

  const fetchOrders = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const values = queryForm.getFieldsValue();
      const res = await get("/api/manage/payment-orders", {
        page: nextPage,
        offset: 20,
        ...values,
      });
      if (res.data.code === 0) {
        setDataSource(res.data.data.list || []);
        setTotal(res.data.data.total || 0);
        setPage(nextPage);
      }
    } catch (error) {
      message.error("获取订单列表失败");
    } finally {
      setLoading(false);
    }
  }, [queryForm]);

  useEffect(() => {
    fetchOrders(1);
  }, [fetchOrders]);

  const handleRefreshProvider = async (record: OrderRecord) => {
    try {
      const res = await post("/api/manage/payment-order-refresh", {
        order_no: record.order_no,
      });
      if (res.data.code === 0) {
        message.success("渠道状态已同步");
        fetchOrders(page);
      }
    } catch (error) {
      message.error("渠道查单失败");
    }
  };

  const openCloseModal = (record: OrderRecord) => {
    setCurrentOrder(record);
    closeForm.setFieldsValue({
      reason: "管理员手动关闭",
    });
    setCloseModalVisible(true);
  };

  const handleCloseOrder = async () => {
    try {
      const values = await closeForm.validateFields();
      const res = await post("/api/manage/payment-order-close", {
        order_no: currentOrder?.order_no,
        ...values,
      });
      if (res.data.code === 0) {
        message.success("订单已关闭");
        setCloseModalVisible(false);
        fetchOrders(page);
      }
    } catch (error) {
      console.error("关闭订单失败", error);
    }
  };

  const columns = [
    {
      title: "订单号",
      dataIndex: "order_no",
      key: "order_no",
      width: 240,
    },
    {
      title: "用户ID",
      dataIndex: "user_id",
      key: "user_id",
      width: 100,
    },
    {
      title: "商品",
      key: "product_type",
      width: 170,
      render: (_: any, record: OrderRecord) =>
        record.product_type === "all_access"
          ? "全站永久解锁"
          : `模块解锁 #${record.module_id ?? "-"}`,
    },
    {
      title: "金额",
      dataIndex: "amount_cents",
      key: "amount_cents",
      width: 100,
      render: (value: number) => `¥${fenToYuan(value)}`,
    },
    {
      title: "平台/渠道",
      key: "platform_channel",
      width: 140,
      render: (_: any, record: OrderRecord) =>
        `${record.platform} / ${record.channel}`,
    },
    {
      title: "渠道状态",
      dataIndex: "provider_status",
      key: "provider_status",
      width: 130,
      render: (value: string) => value || "-",
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (value: string) => {
        const tag = statusTagMap[value] || { color: "default", text: value };
        return <Tag color={tag.color}>{tag.text}</Tag>;
      },
    },
    {
      title: "关闭说明",
      key: "close_reason_label",
      width: 130,
      render: (_: any, record: OrderRecord) => {
        if (record.status !== "closed") return "-";
        if (isAutoExpiredCloseReason(record.fail_reason)) {
          return <Tag color="orange">超时自动关闭</Tag>;
        }
        return <Tag>人工/用户关闭</Tag>;
      },
    },
    {
      title: "支付时间",
      dataIndex: "paid_at",
      key: "paid_at",
      width: 180,
      render: (value: string | null) => value || "-",
    },
    {
      title: "退款金额",
      dataIndex: "refund_amount_cents",
      key: "refund_amount_cents",
      width: 110,
      render: (value: number) =>
        value > 0 ? `¥${fenToYuan(value)}` : "-",
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (value: string | null) => value || "-",
    },
    {
      title: "失败原因",
      dataIndex: "fail_reason",
      key: "fail_reason",
      width: 260,
      render: (value: string) => renderCloseReason(value),
    },
    {
      title: "操作",
      key: "action",
      width: 180,
      render: (_: any, record: OrderRecord) => (
        <Space>
          {["apple_iap", "wechat", "alipay"].includes(record.channel) ? (
            <Button type="link" onClick={() => handleRefreshProvider(record)}>
              同步渠道状态
            </Button>
          ) : null}
          {record.status === "pending" ? (
            <Button type="link" danger onClick={() => openCloseModal(record)}>
              关闭
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Card
        title={
          <Title level={3} style={{ margin: 0 }}>
            支付订单
          </Title>
        }
      >
        <Form form={queryForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item label="订单号" name="order_no">
            <Input placeholder="输入订单号" allowClear />
          </Form.Item>
          <Form.Item label="用户ID" name="user_id">
            <InputNumber min={1} placeholder="用户ID" />
          </Form.Item>
          <Form.Item label="状态" name="status">
            <Select
              allowClear
              style={{ width: 140 }}
              options={[
                { label: "待支付", value: "pending" },
                { label: "已支付", value: "paid" },
                { label: "已关闭", value: "closed" },
              ]}
            />
          </Form.Item>
          <Form.Item label="渠道" name="channel">
            <Select
              allowClear
              style={{ width: 140 }}
              options={[
                { label: "Apple IAP", value: "apple_iap" },
                { label: "微信支付", value: "wechat" },
                { label: "支付宝", value: "alipay" },
                { label: "管理员", value: "admin" },
              ]}
            />
          </Form.Item>
          <Form.Item label="商品" name="product_type">
            <Select
              allowClear
              style={{ width: 160 }}
              options={[
                { label: "模块解锁", value: "module_unlock" },
                { label: "全站永久", value: "all_access" },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={() => fetchOrders(1)}>
                查询
              </Button>
              <Button
                onClick={() => {
                  queryForm.resetFields();
                  fetchOrders(1);
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="说明"
          description="标记为“超时自动关闭”的订单，是系统按未支付超时策略自动关闭的脏单，不代表用户已支付失败。若用户仍要购买，应重新发起新订单。"
        />

        <Table
          loading={loading}
          dataSource={dataSource}
          columns={columns}
          rowKey="id"
          scroll={{ x: 1660 }}
          pagination={{
            current: page,
            pageSize: 20,
            total,
            showTotal: (value) => `共 ${value} 条`,
            onChange: (nextPage) => fetchOrders(nextPage),
          }}
        />
      </Card>

      <Modal
        title={`关闭订单: ${currentOrder?.order_no || ""}`}
        open={closeModalVisible}
        onCancel={() => setCloseModalVisible(false)}
        onOk={handleCloseOrder}
        okText="确认关闭"
      >
        <Form form={closeForm} layout="vertical">
          <Form.Item
            label="关闭原因"
            name="reason"
            rules={[{ required: true, message: "请输入关闭原因" }]}
          >
            <Input placeholder="例如：重复下单、人工取消" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default PaymentOrdersPage;
